import type { ModuleGridType, StudioSystemType } from 'types/studio/items'

import { studioMetresToVendorMm } from './buildVanDerValkFieldLocation'
import { getVanDerValkModuleGridsForBom } from './vanDerValkModuleGrids'

export type VanDerValkRoofPolygonPoint = { x: number; y: number; z: number }

/** Vendor ``roofSlope`` geometry block (``claddingOptions`` merged server-side). */
export type VanDerValkPitchedRoofSlopeGeometry = {
  roofPolygon: VanDerValkRoofPolygonPoint[]
  ridgeIdxs: number[]
  roofType: string
}

export type VanDerValkBuildPitchedRoofSlopeErrorReason =
  | 'no_roof_slope'
  | 'multiple_roof_slopes'
  | 'non_spatial_roof'
  | 'invalid_roof_slope'
  | 'roof_slope_too_shallow'

export type BuildVanDerValkPitchedRoofSlopeFromSystemResult =
  | { ok: true; roofSlope: VanDerValkPitchedRoofSlopeGeometry }
  | { ok: false; reason: VanDerValkBuildPitchedRoofSlopeErrorReason }

export const VDV_MIN_ROOF_POLYGON_INCLINATION_DEG = 5

const MAX_ROOF_POLYGON_VERTICES = 500
const MIN_ROOF_POLYGON_VERTICES = 3
const COLLINEAR_EPSILON_MM = 1
const RIDGE_HEIGHT_TOLERANCE_MM = 1

type StudioPoint3M = { x: number; y: number; z: number }

type VanDerValkFacetNode = {
  uuid?: string
  position?: StudioPoint3M
}

type VanDerValkStructureRef = {
  uuid?: string
  mode?: string
}

type VanDerValkFacetForRoofSlope = {
  uuid?: string
  vertices?: VanDerValkFacetNode[]
  isNonSpatial: () => boolean
  vertexPositions?: () => StudioPoint3M[]
  verticesProjectedOntoPlane?: (positions: StudioPoint3M[]) => StudioPoint3M[] | null
  getStructure?: () => VanDerValkStructureRef | null | undefined
}

const OS_STRUCTURE_MODE_TO_VENDOR_ROOF_TYPE: Record<string, string> = {
  shed: 'SHED_ROOF',
  shed_dormer: 'SHED_ROOF',
  hip: 'HIPPED_ROOF',
  hip_dormer: 'HIPPED_ROOF',
  aframe: 'GABLE_ROOF',
  aframe_dormer: 'GABLE_ROOF',
}

function facetFromGrid(grid: ModuleGridType): VanDerValkFacetForRoofSlope | null {
  const facet = grid.facet as VanDerValkFacetForRoofSlope | null | undefined
  if (!facet || typeof facet.isNonSpatial !== 'function') {
    return null
  }
  return facet
}

function collectUniqueFacets(grids: ModuleGridType[]): VanDerValkFacetForRoofSlope[] {
  const byUuid = new Map<string, VanDerValkFacetForRoofSlope>()
  for (const grid of grids) {
    const facet = facetFromGrid(grid)
    if (!facet) {
      continue
    }
    const key = facet.uuid != null ? String(facet.uuid) : `__anon_${byUuid.size}`
    if (!byUuid.has(key)) {
      byUuid.set(key, facet)
    }
  }
  return [...byUuid.values()]
}

/** Grouping key for managed quick-roof structures (gable/hip/shed share one OsStructure). */
function roofStructureKeyFromFacet(facet: VanDerValkFacetForRoofSlope): string {
  const structure = typeof facet.getStructure === 'function' ? facet.getStructure() : null
  if (structure?.uuid != null) {
    return `structure:${structure.uuid}`
  }
  if (facet.uuid != null) {
    return `facet:${facet.uuid}`
  }
  return 'facet:__anon'
}

function primaryFacetForRoofSlope(grids: ModuleGridType[]): VanDerValkFacetForRoofSlope | null {
  for (const grid of grids) {
    const facet = facetFromGrid(grid)
    if (facet && !facet.isNonSpatial()) {
      return facet
    }
  }
  return null
}

function facetVertexPositionsMetres(facet: VanDerValkFacetForRoofSlope): StudioPoint3M[] | null {
  let positions: StudioPoint3M[] | null = null
  if (typeof facet.vertexPositions === 'function') {
    positions = facet.vertexPositions()
  } else if (Array.isArray(facet.vertices)) {
    positions = facet.vertices
      .map((v) => v?.position)
      .filter(
        (p): p is StudioPoint3M => p != null && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)
      )
  }
  if (!positions?.length) {
    return null
  }
  if (typeof facet.verticesProjectedOntoPlane === 'function') {
    const projected = facet.verticesProjectedOntoPlane(positions)
    if (projected?.length) {
      return projected
    }
  }
  return positions
}

function vendorRoofTypeFromFacet(facet: VanDerValkFacetForRoofSlope): string {
  const mode = typeof facet.getStructure === 'function' ? facet.getStructure()?.mode : undefined
  if (typeof mode === 'string' && OS_STRUCTURE_MODE_TO_VENDOR_ROOF_TYPE[mode]) {
    return OS_STRUCTURE_MODE_TO_VENDOR_ROOF_TYPE[mode]
  }
  return 'SHED_ROOF'
}

/**
 * Ridge indices: all corners on the highest-y edge (vendor Y = height).
 * See Van der Valk pitched-roof BOM docs for ``ridgeIdxs`` semantics.
 */
export function deriveVanDerValkRidgeIdxsFromRoofPolygon(
  roofPolygon: VanDerValkRoofPolygonPoint[],
  toleranceMm = RIDGE_HEIGHT_TOLERANCE_MM
): number[] {
  if (roofPolygon.length === 0) {
    return []
  }
  const maxY = Math.max(...roofPolygon.map((p) => p.y))
  return roofPolygon.reduce<number[]>((indices, point, index) => {
    if (maxY - point.y <= toleranceMm) {
      indices.push(index)
    }
    return indices
  }, [])
}

function crossProduct(a: VanDerValkRoofPolygonPoint, b: VanDerValkRoofPolygonPoint): VanDerValkRoofPolygonPoint {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  }
}

function subtract(a: VanDerValkRoofPolygonPoint, b: VanDerValkRoofPolygonPoint): VanDerValkRoofPolygonPoint {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }
}

function vectorLength(v: VanDerValkRoofPolygonPoint): number {
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z)
}

/**
 * Roof plane inclination in degrees from horizontal (vendor Y = height).
 * Uses the first three non-collinear polygon vertices.
 */
export function computeVanDerValkRoofPolygonInclinationDeg(roofPolygon: VanDerValkRoofPolygonPoint[]): number | null {
  if (roofPolygon.length < 3) {
    return null
  }
  const p0 = roofPolygon[0]
  for (let i = 1; i < roofPolygon.length - 1; i++) {
    const v1 = subtract(roofPolygon[i], p0)
    for (let j = i + 1; j < roofPolygon.length; j++) {
      const v2 = subtract(roofPolygon[j], p0)
      const normal = crossProduct(v1, v2)
      const len = vectorLength(normal)
      if (len <= COLLINEAR_EPSILON_MM) {
        continue
      }
      const ny = Math.abs(normal.y / len)
      return Math.acos(Math.min(1, Math.max(0, ny))) * (180 / Math.PI)
    }
  }
  return null
}

function roofPlaneUpNormalUnit(roofPolygon: VanDerValkRoofPolygonPoint[]): VanDerValkRoofPolygonPoint | null {
  if (roofPolygon.length < 3) {
    return null
  }
  const p0 = roofPolygon[0]
  for (let i = 1; i < roofPolygon.length - 1; i++) {
    const v1 = subtract(roofPolygon[i], p0)
    for (let j = i + 1; j < roofPolygon.length; j++) {
      const v2 = subtract(roofPolygon[j], p0)
      const normal = crossProduct(v1, v2)
      const len = vectorLength(normal)
      if (len <= COLLINEAR_EPSILON_MM) {
        continue
      }
      const unit = {
        x: normal.x / len,
        y: normal.y / len,
        z: normal.z / len,
      }
      if (unit.y < 0) {
        return { x: -unit.x, y: -unit.y, z: -unit.z }
      }
      return unit
    }
  }
  return null
}

/**
 * Horizontal downslope direction on the roof footprint (vendor X–Z plane, unit length).
 * Row-step azimuth must follow this, not panel facing azimuth (they differ on gable facets).
 */
export function computeVanDerValkRoofDownslopeHorizontalUnit(
  roofPolygon: VanDerValkRoofPolygonPoint[]
): { x: number; z: number } | null {
  const normal = roofPlaneUpNormalUnit(roofPolygon)
  if (!normal) {
    return null
  }
  // Steepest descent of g=(0,-1,0) on the roof plane: t = g - (g·n)n, horizontal part only.
  const gDotN = -normal.y
  const dx = 0 - gDotN * normal.x
  const dz = 0 - gDotN * normal.z
  const len = Math.hypot(dx, dz)
  if (len < 1e-9) {
    return null
  }
  return { x: dx / len, z: dz / len }
}

/** Snap a vendor mm point onto the roof polygon plane (required for pitched field location). */
export function projectVanDerValkPointOntoRoofPlane(
  point: VanDerValkRoofPolygonPoint,
  roofPolygon: VanDerValkRoofPolygonPoint[]
): VanDerValkRoofPolygonPoint | null {
  const normal = roofPlaneUpNormalUnit(roofPolygon)
  if (!normal) {
    return null
  }
  const anchor = roofPolygon[0]
  const vx = point.x - anchor.x
  const vy = point.y - anchor.y
  const vz = point.z - anchor.z
  const dist = vx * normal.x + vy * normal.y + vz * normal.z
  return {
    x: Math.round(point.x - dist * normal.x),
    y: Math.round(point.y - dist * normal.y),
    z: Math.round(point.z - dist * normal.z),
  }
}

export function getVanDerValkRoofPlaneUpNormalUnit(
  roofPolygon: VanDerValkRoofPolygonPoint[]
): VanDerValkRoofPolygonPoint | null {
  return roofPlaneUpNormalUnit(roofPolygon)
}

export function distanceVanDerValkPointToRoofPlaneMm(
  point: VanDerValkRoofPolygonPoint,
  roofPolygon: VanDerValkRoofPolygonPoint[]
): number | null {
  const normal = roofPlaneUpNormalUnit(roofPolygon)
  if (!normal) {
    return null
  }
  const anchor = roofPolygon[0]
  const vx = point.x - anchor.x
  const vy = point.y - anchor.y
  const vz = point.z - anchor.z
  return Math.abs(vx * normal.x + vy * normal.y + vz * normal.z)
}

type VanDerValkLocationTriple = {
  base: VanDerValkRoofPolygonPoint
  tangent: VanDerValkRoofPolygonPoint
  azimuth: VanDerValkRoofPolygonPoint
}

function subtractRoofPoint(
  a: VanDerValkRoofPolygonPoint,
  b: VanDerValkRoofPolygonPoint
): VanDerValkRoofPolygonPoint {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }
}

function projectVectorOntoRoofPlane(
  vector: VanDerValkRoofPolygonPoint,
  normal: VanDerValkRoofPolygonPoint
): VanDerValkRoofPolygonPoint {
  const vn = vector.x * normal.x + vector.y * normal.y + vector.z * normal.z
  return {
    x: vector.x - vn * normal.x,
    y: vector.y - vn * normal.y,
    z: vector.z - vn * normal.z,
  }
}

function vectorLengthMm(v: VanDerValkRoofPolygonPoint): number {
  return Math.hypot(v.x, v.y, v.z)
}

/**
 * Rebuild field location so base, tangent, and azimuth are co-planar with ``roofPolygon``.
 * Preserves step lengths from ``hints`` while forcing all three points onto the roof plane.
 */
export function buildVanDerValkFieldLocationOnRoofPlane(
  hints: VanDerValkLocationTriple,
  roofPolygon: VanDerValkRoofPolygonPoint[]
): VanDerValkLocationTriple | null {
  const normal = roofPlaneUpNormalUnit(roofPolygon)
  if (!normal) {
    return null
  }

  const base = projectVanDerValkPointOntoRoofPlane(hints.base, roofPolygon)
  if (!base) {
    return null
  }

  const tangentHint = subtractRoofPoint(hints.tangent, hints.base)
  const azimuthHint = subtractRoofPoint(hints.azimuth, hints.base)
  const tangentHintLen = vectorLengthMm(tangentHint)
  const azimuthHintLen = vectorLengthMm(azimuthHint)
  if (tangentHintLen < 1 || azimuthHintLen < 1) {
    return null
  }

  const tangentOnPlane = projectVectorOntoRoofPlane(tangentHint, normal)
  const azimuthOnPlane = projectVectorOntoRoofPlane(azimuthHint, normal)
  const tangentOnPlaneLen = vectorLengthMm(tangentOnPlane)
  const azimuthOnPlaneLen = vectorLengthMm(azimuthOnPlane)
  if (tangentOnPlaneLen < 1e-6 || azimuthOnPlaneLen < 1e-6) {
    return null
  }

  const tangentScale = tangentHintLen / tangentOnPlaneLen
  const azimuthScale = azimuthHintLen / azimuthOnPlaneLen
  const tangent = {
    x: Math.round(base.x + tangentOnPlane.x * tangentScale),
    y: Math.round(base.y + tangentOnPlane.y * tangentScale),
    z: Math.round(base.z + tangentOnPlane.z * tangentScale),
  }
  const azimuth = {
    x: Math.round(base.x + azimuthOnPlane.x * azimuthScale),
    y: Math.round(base.y + azimuthOnPlane.y * azimuthScale),
    z: Math.round(base.z + azimuthOnPlane.z * azimuthScale),
  }

  return { base, tangent, azimuth }
}

export function validateVanDerValkPitchedRoofSlopeGeometry(
  roofPolygon: VanDerValkRoofPolygonPoint[],
  ridgeIdxs: number[]
): VanDerValkBuildPitchedRoofSlopeErrorReason | null {
  if (ridgeIdxs.length === 0) {
    return 'invalid_roof_slope'
  }
  const ridgeSet = new Set<number>()
  for (const idx of ridgeIdxs) {
    if (!Number.isInteger(idx) || idx < 0 || idx >= roofPolygon.length) {
      return 'invalid_roof_slope'
    }
    ridgeSet.add(idx)
  }
  if (ridgeSet.size !== ridgeIdxs.length) {
    return 'invalid_roof_slope'
  }
  if (roofPolygon.length >= MIN_ROOF_POLYGON_VERTICES) {
    const hasEave = roofPolygon.some((_, index) => !ridgeSet.has(index))
    if (!hasEave) {
      return 'invalid_roof_slope'
    }
    const ridgeYs = ridgeIdxs.map((index) => roofPolygon[index].y)
    const eaveYs = roofPolygon.filter((_, index) => !ridgeSet.has(index)).map((point) => point.y)
    if (eaveYs.length > 0 && Math.min(...ridgeYs) <= Math.max(...eaveYs) + RIDGE_HEIGHT_TOLERANCE_MM) {
      return 'invalid_roof_slope'
    }
  }
  const inclination = computeVanDerValkRoofPolygonInclinationDeg(roofPolygon)
  if (inclination == null || inclination <= VDV_MIN_ROOF_POLYGON_INCLINATION_DEG) {
    return 'roof_slope_too_shallow'
  }
  return null
}

/** Signed area of roof footprint in vendor horizontal X–Z plane (mm²); positive matches doc examples. */
export function signedAreaXzMm(roofPolygon: VanDerValkRoofPolygonPoint[]): number {
  if (roofPolygon.length < 3) {
    return 0
  }
  let sum = 0
  for (let i = 0; i < roofPolygon.length; i++) {
    const j = (i + 1) % roofPolygon.length
    sum += roofPolygon[i].x * roofPolygon[j].z - roofPolygon[j].x * roofPolygon[i].z
  }
  return sum / 2
}

/**
 * Vendor pitched ``roofPolygon`` footprint uses consistent X–Z winding (doc examples are all positive signed area).
 * Reverse vertex order and remap ``ridgeIdxs`` when Studio facet order is opposite.
 */
export function ensureVendorRoofPolygonWindingInXz(
  roofPolygon: VanDerValkRoofPolygonPoint[],
  ridgeIdxs: number[]
): { roofPolygon: VanDerValkRoofPolygonPoint[]; ridgeIdxs: number[]; reversed: boolean } {
  if (signedAreaXzMm(roofPolygon) >= 0) {
    return { roofPolygon, ridgeIdxs, reversed: false }
  }
  const n = roofPolygon.length
  const reversedPolygon = [...roofPolygon].reverse()
  const remappedRidgeIdxs = ridgeIdxs.map((idx) => n - 1 - idx).sort((a, b) => a - b)
  return { roofPolygon: reversedPolygon, ridgeIdxs: remappedRidgeIdxs, reversed: true }
}

export function buildVanDerValkPitchedRoofSlopeFromFacet(
  facet: VanDerValkFacetForRoofSlope
): BuildVanDerValkPitchedRoofSlopeFromSystemResult {
  if (facet.isNonSpatial()) {
    return { ok: false, reason: 'non_spatial_roof' }
  }
  const positions = facetVertexPositionsMetres(facet)
  if (!positions || positions.length < MIN_ROOF_POLYGON_VERTICES) {
    return { ok: false, reason: 'invalid_roof_slope' }
  }
  if (positions.length > MAX_ROOF_POLYGON_VERTICES) {
    return { ok: false, reason: 'invalid_roof_slope' }
  }

  const roofPolygonRaw = positions.map((p) => studioMetresToVendorMm(p))
  const ridgeIdxsRaw = deriveVanDerValkRidgeIdxsFromRoofPolygon(roofPolygonRaw)
  const roofType = vendorRoofTypeFromFacet(facet)
  const { roofPolygon, ridgeIdxs } = ensureVendorRoofPolygonWindingInXz(roofPolygonRaw, ridgeIdxsRaw)

  const validationError = validateVanDerValkPitchedRoofSlopeGeometry(roofPolygon, ridgeIdxs)
  if (validationError) {
    return { ok: false, reason: validationError }
  }

  return {
    ok: true,
    roofSlope: {
      roofPolygon,
      ridgeIdxs,
      roofType,
    },
  }
}

export function buildVanDerValkPitchedRoofSlopeFromSystem(
  system: StudioSystemType | null | undefined
): BuildVanDerValkPitchedRoofSlopeFromSystemResult {
  const grids = getVanDerValkModuleGridsForBom(system)
  if (!grids.length) {
    return { ok: false, reason: 'no_roof_slope' }
  }

  const facets = collectUniqueFacets(grids)
  if (facets.length === 0) {
    return { ok: false, reason: 'no_roof_slope' }
  }

  const structureKeys = [...new Set(facets.map(roofStructureKeyFromFacet))]
  if (structureKeys.length > 1) {
    return { ok: false, reason: 'multiple_roof_slopes' }
  }

  for (const facet of facets) {
    const facetResult = buildVanDerValkPitchedRoofSlopeFromFacet(facet)
    if (!facetResult.ok) {
      return facetResult
    }
  }

  const primaryFacet = primaryFacetForRoofSlope(grids)
  if (!primaryFacet) {
    return { ok: false, reason: 'no_roof_slope' }
  }

  return buildVanDerValkPitchedRoofSlopeFromFacet(primaryFacet)
}

export function buildVanDerValkPitchedRoofSlopeFromModuleGrid(
  grid: ModuleGridType
): BuildVanDerValkPitchedRoofSlopeFromSystemResult {
  const facet = facetFromGrid(grid)
  if (!facet) {
    return { ok: false, reason: 'no_roof_slope' }
  }
  return buildVanDerValkPitchedRoofSlopeFromFacet(facet)
}

export function vanDerValkPitchedRoofSlopeErrorMessage(reason: VanDerValkBuildPitchedRoofSlopeErrorReason): string {
  const translate = window.translate?.bind(window)
  switch (reason) {
    case 'no_roof_slope':
      return (
        translate?.('mount_vdv_no_roof_outline') ??
        'Draw a roof and place panels on it before generating the Van der Valk BOM.'
      )
    case 'multiple_roof_slopes':
      return (
        translate?.('mount_vdv_multiple_roof_outlines') ??
        'Van der Valk BOM requires all active panel groups on the same roof section.'
      )
    case 'non_spatial_roof':
      return (
        translate?.('mount_vdv_non_spatial_roof') ??
        'Van der Valk requires panels to be placed on a manually drawn roof facet. Draw the roof using Tools (top right), then slightly drag the panels onto the new roof.'
      )
    case 'roof_slope_too_shallow':
      return (
        translate?.('mount_vdv_roof_slope_too_shallow') ??
        'Roof slope must be greater than 5° for the Van der Valk pitched-roof BOM.'
      )
    case 'invalid_roof_slope':
    default:
      return (
        translate?.('mount_vdv_invalid_roof_outline') ??
        'Roof outline is invalid. Check the drawn roof perimeter and try again.'
      )
  }
}
